/**
 * Token Statistics Page
 * Comprehensive view of token usage across the platform
 */
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PageShell } from '@/components/common/PageShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { 
  Coins, 
  Download, 
  RefreshCw,
  Activity,
  Layers,
  ArrowDownToLine,
  ArrowUpFromLine,
  Zap,
  DollarSign,
  AlertTriangle,
} from 'lucide-react'
import { TokenUsageChart } from '@/components/tokens/TokenUsageChart'
import { TopSessionsByTokens } from '@/components/tokens/TopSessionsByTokens'
import { ThresholdBadge } from '@/components/tokens/ThresholdBadge'
import { useTokenStats, useTokenTimeSeries, useTopSessionsByTokens, useOverallThresholdStatus } from '@/api/orchestrator'
import { formatCompactNumber, getAggregateTokenThresholdColor, cn } from '@/lib/utils'
import { PERIOD_OPTIONS, getDateRange, type PeriodValue } from '@/lib/constants'
import type { ModelCostBreakdown } from '@/types/chatbot'
import type { OverallModelThresholdStatus } from '@/types/pricing'

/**
 * Summary stat card component
 */
function StatCard({ 
  title, 
  value, 
  subtitle,
  icon: Icon,
  color = 'default',
  isLoading = false
}: { 
  title: string
  value: number | string
  subtitle?: string
  icon: React.ElementType
  color?: 'default' | 'blue' | 'green' | 'purple' | 'orange' | 'threshold'
  isLoading?: boolean
}) {
  const colorClasses = {
    default: 'text-foreground',
    blue: 'text-blue-600',
    green: 'text-emerald-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    threshold: typeof value === 'number' ? getAggregateTokenThresholdColor(value) : 'text-foreground',
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className={cn('text-2xl font-bold', colorClasses[color])}>
              {typeof value === 'number' ? formatCompactNumber(value) : value}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Model breakdown bar visualization
 */
function ModelBreakdownChart({ 
  modelBreakdown, 
  costByModel,
  thresholdStatus,
  isLoading 
}: { 
  modelBreakdown: Array<{ modelName: string; inputTokens: number; outputTokens: number; totalTokens: number; interactionCount: number }>
  costByModel?: ModelCostBreakdown[] | null
  thresholdStatus?: OverallModelThresholdStatus[] | null
  isLoading: boolean 
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Token Distribution by Model</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!modelBreakdown || modelBreakdown.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Token Distribution by Model</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No token data available</p>
        </CardContent>
      </Card>
    )
  }

  // Create a map of model costs for quick lookup
  const costMap = new Map<string, ModelCostBreakdown>()
  if (costByModel) {
    costByModel.forEach(c => costMap.set(c.modelName, c))
  }

  // Create a map of threshold status for quick lookup
  const thresholdMap = new Map<string, OverallModelThresholdStatus>()
  if (thresholdStatus) {
    thresholdStatus.forEach(t => thresholdMap.set(t.modelName, t))
  }

  const total = modelBreakdown.reduce((sum, m) => sum + m.totalTokens, 0)
  const colors = [
    'bg-primary',
    'bg-emerald-500', 
    'bg-violet-400',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Token Distribution by Model</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TooltipProvider>
          {modelBreakdown.map((model, index) => {
            const percentage = total > 0 ? (model.totalTokens / total) * 100 : 0
            const modelCost = costMap.get(model.modelName)
            const modelThreshold = thresholdMap.get(model.modelName)
            return (
              <div key={model.modelName} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate max-w-[200px]" title={model.modelName}>
                    {model.modelName}
                  </span>
                  <div className="flex items-center gap-3">
                    {modelCost && (
                      <span className="text-amber-600 font-medium flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {modelCost.totalCost.toFixed(2)}
                      </span>
                    )}
                    <span className="text-muted-foreground">
                      {formatCompactNumber(model.totalTokens)} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={cn('h-full rounded-full transition-all', colors[index % colors.length])}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ArrowDownToLine className="h-3 w-3" />
                    In: {formatCompactNumber(model.inputTokens)}
                    {modelCost && <span className="text-amber-600 ml-1">(${modelCost.inputCost.toFixed(2)})</span>}
                    {modelThreshold && (
                      <ThresholdBadge
                        status={modelThreshold.inputStatus ?? 'none'}
                        current={modelThreshold.inputTokens}
                        warningThreshold={modelThreshold.inputWarning ?? undefined}
                        criticalThreshold={modelThreshold.inputCritical ?? undefined}
                        label="Overall Input"
                      />
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowUpFromLine className="h-3 w-3" />
                    Out: {formatCompactNumber(model.outputTokens)}
                    {modelCost && <span className="text-amber-600 ml-1">(${modelCost.outputCost.toFixed(2)})</span>}
                    {modelThreshold && (
                      <ThresholdBadge
                        status={modelThreshold.outputStatus ?? 'none'}
                        current={modelThreshold.outputTokens}
                        warningThreshold={modelThreshold.outputWarning ?? undefined}
                        criticalThreshold={modelThreshold.outputCritical ?? undefined}
                        label="Overall Output"
                      />
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    {model.interactionCount} calls
                  </span>
                </div>
              </div>
            )
          })}
        </TooltipProvider>
      </CardContent>
    </Card>
  )
}

/**
 * Agent breakdown visualization
 */
function AgentBreakdownChart({ 
  agentBreakdown, 
  isLoading 
}: { 
  agentBreakdown: Array<{ agent: string; inputTokens: number; outputTokens: number; totalTokens: number; interactionCount: number }>
  isLoading: boolean 
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Token Distribution by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!agentBreakdown || agentBreakdown.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Token Distribution by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No token data available</p>
        </CardContent>
      </Card>
    )
  }

  const total = agentBreakdown.reduce((sum, a) => sum + a.totalTokens, 0)
  const agentColors: Record<string, string> = {
    'Orchestrator': 'bg-primary',
    'OpenHands': 'bg-emerald-500',
    'Unknown': 'bg-gray-500',
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Token Distribution by Agent</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {agentBreakdown.map((agent) => {
          const percentage = total > 0 ? (agent.totalTokens / total) * 100 : 0
          return (
            <div key={agent.agent} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{agent.agent}</span>
                <span className="text-muted-foreground">
                  {formatCompactNumber(agent.totalTokens)} ({percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={cn('h-full rounded-full transition-all', agentColors[agent.agent] ?? 'bg-gray-500')}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ArrowDownToLine className="h-3 w-3" />
                  In: {formatCompactNumber(agent.inputTokens)}
                </span>
                <span className="flex items-center gap-1">
                  <ArrowUpFromLine className="h-3 w-3" />
                  Out: {formatCompactNumber(agent.outputTokens)}
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="h-3 w-3" />
                  {agent.interactionCount} calls
                </span>
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

/**
 * Token Statistics Page Component
 */
export function TokenStatsPage() {
  const [period, setPeriod] = useState<PeriodValue>('30d')
  
  const dateRange = useMemo(() => getDateRange(period), [period])

  // Fetch data
  const { 
    data: stats, 
    isLoading: statsLoading, 
    refetch: refetchStats
  } = useTokenStats({
    from: dateRange.from,
    to: dateRange.to
  })

  const { 
    data: timeSeries, 
    isLoading: timeSeriesLoading, 
    error: timeSeriesError,
    refetch: refetchTimeSeries
  } = useTokenTimeSeries({
    from: dateRange.from,
    to: dateRange.to,
    granularity: 'day'
  })

  const { 
    data: topSessions, 
    isLoading: topSessionsLoading, 
    error: topSessionsError,
    refetch: refetchTopSessions
  } = useTopSessionsByTokens({
    limit: 10,
    from: dateRange.from,
    to: dateRange.to
  })

  const {
    data: thresholdStatus,
    refetch: refetchThresholdStatus
  } = useOverallThresholdStatus()

  const handleRefresh = () => {
    refetchStats()
    refetchTimeSeries()
    refetchTopSessions()
    refetchThresholdStatus()
  }

  // Use new API structure
  const grandTotal = stats?.grandTotal ?? 0
  const avgPerSession = stats?.sessionCount ? Math.round(grandTotal / stats.sessionCount) : 0
  const avgPerInteraction = stats?.interactionCount ? Math.round(grandTotal / stats.interactionCount) : 0

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Coins className="h-6 w-6" />
              Token Usage Statistics
            </h1>
            <p className="text-muted-foreground text-sm">
              Monitor LLM token consumption across all sessions
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Period selector */}
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodValue)}>
              <SelectTrigger className="w-[160px]" data-testid="period-selector">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh button */}
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>

            {/* Export button (placeholder) */}
            <Button variant="outline" size="sm" disabled>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="token-stat-cards">
          <StatCard
            title="Total Tokens"
            value={grandTotal}
            subtitle={PERIOD_OPTIONS.find(p => p.value === period)?.label}
            icon={Coins}
            color="threshold"
            isLoading={statsLoading}
          />
          <StatCard
            title="Estimated Cost"
            value={stats?.totalEstimatedCost != null ? `$${stats.totalEstimatedCost.toFixed(2)}` : '-'}
            subtitle={stats?.currency ?? 'USD'}
            icon={DollarSign}
            color="purple"
            isLoading={statsLoading}
          />
          <StatCard
            title="Sessions"
            value={stats?.sessionCount ?? 0}
            subtitle="With token data"
            icon={Activity}
            isLoading={statsLoading}
          />
          <StatCard
            title="Avg per Session"
            value={avgPerSession}
            subtitle="Tokens"
            icon={Layers}
            isLoading={statsLoading}
          />
          <StatCard
            title="Input Tokens"
            value={stats?.totalInputTokens ?? 0}
            subtitle="Prompts sent"
            icon={ArrowDownToLine}
            color="blue"
            isLoading={statsLoading}
          />
          <StatCard
            title="Output Tokens"
            value={stats?.totalOutputTokens ?? 0}
            subtitle="Responses received"
            icon={ArrowUpFromLine}
            color="green"
            isLoading={statsLoading}
          />
        </div>

        {/* Second row - Interactions stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Total Interactions"
            value={stats?.interactionCount ?? 0}
            subtitle="LLM API calls"
            icon={Zap}
            color="purple"
            isLoading={statsLoading}
          />
          <StatCard
            title="Avg per Interaction"
            value={avgPerInteraction}
            subtitle="Tokens"
            icon={Activity}
            color="orange"
            isLoading={statsLoading}
          />
          <StatCard
            title="Models Used"
            value={stats?.modelBreakdown?.length ?? 0}
            subtitle="Different models"
            icon={Layers}
            isLoading={statsLoading}
          />
        </div>

        {/* Missing pricing warning */}
        {stats?.modelsWithoutPricing && stats.modelsWithoutPricing.length > 0 && (
          <Alert variant="default" className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Some models don't have pricing configured</AlertTitle>
            <AlertDescription className="text-amber-700">
              <p>The following models were used but have no pricing: <strong>{stats.modelsWithoutPricing.join(', ')}</strong></p>
              <p className="mt-1">Cost estimates may be incomplete. <Link to="/configuration/model-pricing" className="underline hover:no-underline">Configure Pricing →</Link></p>
            </AlertDescription>
          </Alert>
        )}

        {/* Model and Agent breakdown side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ModelBreakdownChart 
            modelBreakdown={stats?.modelBreakdown ?? []}
            costByModel={stats?.costByModel}
            thresholdStatus={thresholdStatus?.models}
            isLoading={statsLoading}
          />
          <AgentBreakdownChart 
            agentBreakdown={stats?.agentBreakdown ?? []}
            isLoading={statsLoading}
          />
        </div>

        {/* Chart */}
        <TokenUsageChart
          data={timeSeries?.data ?? []}
          isLoading={timeSeriesLoading}
          error={timeSeriesError?.message}
          title={`Token Usage - ${PERIOD_OPTIONS.find(p => p.value === period)?.label}`}
          granularity="day"
          height={350}
        />

        {/* Top Sessions */}
        <TopSessionsByTokens
          sessions={topSessions?.sessions ?? []}
          isLoading={topSessionsLoading}
          error={topSessionsError?.message}
          title="Top Sessions by Token Usage"
          limit={10}
        />
      </div>
    </PageShell>
  )
}

export default TokenStatsPage
